﻿import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Stack,
  FormControlLabel,
  TextField,
  Container,
  DialogContent,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { SearchIcon } from "@/ImageRepo";
import { selectWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import { selectHomeDateFilter } from "@/features/dateFilterSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectConnectionProfile } from "@/features/settings/settingsSlice";
import "@/components/ConnectionsBar/ConnectionsBar.css";
import "./WorkflowSettingsPopup.css";
import dayjs from "dayjs";
import { SettingsCard } from "./WorkflowSettingsPopup";
import {
  ShepCheckbox,
  ShepTab,
  ShepTabs,
} from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import { ShepDataGridPro } from "@/components/GenericComponents/ShepComponents";
import { createAlert } from "@/features/homepage/alertSliceList";

const PreviewWorkflowScript = ({
  isGlobal,
  onClose,
  workflowName,
  scripts,
}) => {
  const dispatch = useDispatch();
  const selectedOperationId = useSelector(selectWorkflow);
  let connectionProfile = useSelector(selectConnectionProfile);
  const [tabValue, setTabValue] = useState(0);
  const [previewTab, setPreviewTab] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [sourceArtifactDetails, setSourceArtifactDetails] = useState("");
  const [beforeProcessingDetails, setBeforeProcessingDetails] = useState("");
  const [afterProcessingDetails, setAfterProcessingDetails] = useState("");
  const [targetArtifactDetails, setTargetArtifactDetails] = useState("");
  const [artifactRows, setArtifactRows] = useState([]);
  const { startDate, endDate } = useSelector(selectHomeDateFilter) || {};
  const [rows, setRows] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [previewClicked, setPreviewClicked] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (tabValue === 1) {
      setFilteredRows(rows.filter((r) => Number(r.Timing) === 1));
    } else {
      setFilteredRows(rows.filter((r) => Number(r.Timing) === 0));
    }
  }, [tabValue, rows]);

  useEffect(() => {
    setRows(
      scripts.map((s) => {
        return { ...s, Id: s.id, isIncluded: true };
      }),
    );
  }, [scripts]);

  useEffect(() => {
    getRecords();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePreviewTabChange = (event, newValue) => {
    setPreviewTab(newValue);
  };

  const handlePreview = async () => {
    if (selectedArtifact == null) {
      dispatch(
        createAlert({
          status: "Info",
          alertType: "single",
          message: "Please select an artifact to preview",
        }),
      );
      return;
    }
    if (rows === null || rows.filter((r) => r.isIncluded).length === 0) {
      dispatch(
        createAlert({
          status: "Info",
          alertType: "single",
          message: "No scripts selected to preview",
        }),
      );
      return;
    }
    setPreviewClicked(true);
    setBeforeProcessingDetails("");
    setAfterProcessingDetails("");
    setTargetArtifactDetails("");
    setErrorMsg("");
    const scriptsToRun = rows
      .filter((r) => r.isIncluded)
      .map((x) => ({
        Id: x.id,
        Name: x.Name,
        LastUpdated: x.LastUpdated,
        UserName: x.UserName,
        IsEnabled: Boolean(x.IsEnabled),
        Script: x.Script,
        Timing: x.Timing,
      }));
    await axios
      .post(`/operations/preview-workflow-scripts`, scriptsToRun, {
        params: {
          operationId: selectedOperationId,
          connectionPairId: isGlobal
            ? selectedArtifact.ConnectionPairId
            : (connectionProfile?.ConnectionPairId ?? -1),
          connectionId: selectedArtifact.SourceConnectionId,
          sourceArtifactDictionaryDataId:
            selectedArtifact.SourceArtifactDictionaryDataId,
        },
      })
      .then((response) => {
        const {
          BeforeProcessingDetails,
          AfterProcessingDetails,
          TargetArtifactDetails,
          ErrorMsg,
        } = response.data;
        setBeforeProcessingDetails(BeforeProcessingDetails);
        setAfterProcessingDetails(AfterProcessingDetails);
        setTargetArtifactDetails(TargetArtifactDetails);
        if (ErrorMsg && ErrorMsg !== "") {
          setErrorMsg(ErrorMsg);
        }
      })
      .catch((err) => {
        var errorMsg = err.response.data;
        setSourceArtifactDetails(sourceArtifactDetails + errorMsg);
      });
    setPreviewClicked(false);
  };

  const handleTextChange = (e) => {
    setSearchString(e.target.value);
    if (e.target.value === "") {
      getRecords();
    }
  };

  const getRecords = async () => {
    try {
      const response = await axios.get("/operationsmanager/manager-records/", {
        params: {
          operationId: selectedOperationId,
          startDate: startDate,
          endDate: endDate,
          filters: "",
          searchFilter: searchString,
          connectionId: -1,
          clearCache: false,
          connectionPairId: isGlobal
            ? -1
            : (connectionProfile?.ConnectionPairId ?? -1),
        },
      });
      let data = [];
      if (response && response.data.Records.length > 0) {
        data = response.data.Records.map((x, i) => ({ id: i, ...x }));
      }
      setArtifactRows(data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleArtifactSelected = async (row) => {
    setSelectedArtifact(row);
    // preview source artifact details before any scripts are run
    await axios
      .post(`/operations/source-artifact-preview`, null, {
        params: {
          operationId: selectedOperationId,
          connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
          sourceArtifactDictionaryDataId: row.SourceArtifactDictionaryDataId,
        },
      })
      .then((response) => {
        const sourceArtifactJson = response.data;
        setSourceArtifactDetails(sourceArtifactJson);
      })
      .catch((err) => {
        var errorMsg = "Error fetching Source Artifact Data";
        setSourceArtifactDetails(errorMsg);
      });
    setBeforeProcessingDetails("");
    setAfterProcessingDetails("");
    setTargetArtifactDetails("");
    setErrorMsg("");
  };

  const columns = [
    {
      field: "Name",
      headerName: "Script Name",
      width: 300,
      sortable: false,
    },
    {
      field: "Include",
      headerName: "Include",
      width: 110,
      sortable: false,
      renderCell: (params) => {
        return (
          <FormControlLabel
            control={
              <ShepCheckbox
                checked={params.row.isIncluded}
                onChange={(event) => {
                  const ischecked = event.target.checked;

                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === params.row.id
                        ? { ...r, isIncluded: ischecked }
                        : r,
                    ),
                  );
                }}
              />
            }
            style={{ marginLeft: 5, minWidth: 180 }}
          />
        );
      },
    },
  ];

  const artifactColumns = [
    {
      field: "From",
      headerName: "From",
      width: 270,
      sortable: false,
      renderCell: (params) => (
        <Typography>{params.row.SourceReference ?? "-"}</Typography>
      ),
    },
    {
      field: "To",
      headerName: "To",
      width: 240,
      sortable: false,
      renderCell: (params) => (
        <Typography>{params.row.TargetReference ?? "-"}</Typography>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      width: 235,
      sortable: false,
      renderCell: (params) => (
        <Stack>
          <Stack
            direction="row"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Typography
              style={{
                fontWeight: "var(--font-semi-bold)",
                paddingRight: "5px",
              }}
            >
              {"Received:"}
            </Typography>
            <Typography>
              {params.row.FromDate
                ? dayjs(params.row.FromDate)
                    .utc(true)
                    .local()
                    .format("YYYY.MM.DD HH:mm")
                : "-"}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography
              style={{
                fontWeight: "var(--font-semi-bold)",
                paddingRight: "5px",
              }}
            >
              {"Posted:"}
            </Typography>
            <Typography>
              {params.row.ToDate
                ? dayjs(params.row.ToDate)
                    .utc(true)
                    .local()
                    .format("YYYY.MM.DD HH:mm")
                : "-"}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
  ];

  return (
    <>
      {
        <Stack direction={"column"}>
          <>
            <SettingsCard
              title={"Script"}
              description={"Create scripts for pre and post processing."}
              className="config-options-card scheduler-settings-card"
            >
              <div className="grid-wrapper">
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                ></div>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    marginTop: "40px",
                  }}
                ></Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-around",
                  }}
                >
                  <Container sx={{ width: "42%", marginTop: "-45px" }}>
                    <Stack style={{ paddingRight: "60px" }}>
                      <Typography
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontWeight: "var(--font-semi-bold)",
                          fontSize: "24px",
                          borderRadius: "15px 15px 0 0",
                          marginBottom: "35px",
                        }}
                      >
                        Preview Output
                      </Typography>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: "var(--primary-main)",
                        }}
                      >
                        <ShepTabs
                          variant="primary"
                          value={tabValue}
                          onChange={handleTabChange}
                          sx={{
                            "& .MuiTab-root": {},
                          }}
                        >
                          <ShepTab
                            variant="primary"
                            label={"Before Processing"}
                            sx={{ width: "45%" }}
                          ></ShepTab>
                          <ShepTab
                            variant="primary"
                            label={"After Processing"}
                            sx={{ width: "45%" }}
                          ></ShepTab>
                        </ShepTabs>
                      </Box>
                      <ShepDataGridPro
                        isDataLoaded={true}
                        rows={filteredRows}
                        rowCount={filteredRows.length}
                        columns={columns}
                        columnHeaderHeight={78}
                        rowHeight={100}
                        whiteHeader={true}
                        checkboxSelection={false}
                        disableMultipleRowSelection={false}
                        disableColumnMenu={true}
                        disableColumnFilter={true}
                        disableRowSelectionOnClick={false}
                        disableColumnReorder={true}
                        hideFooterSelectedRowCount={true}
                        pagination
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 3, page: 1 },
                          },
                        }}
                        pageSizeOptions={[6]}
                        sx={{
                          marginBottom: "55px",
                          color: "var(#0c0c0c)",
                          fontSize: "var(--font-sm)",
                          fontFamily: "var(--font-family)",
                          borderTopLeftRadius: "12px",
                          borderTopRightRadius: "12px",
                          backgroundColor: "var(--default-light)",
                          border: "none",
                          "& .MuiDataGrid-cell": {
                            borderBottom: "none",
                          },
                          "& .MuiDataGrid-row": {
                            overflow: "hidden",
                          },
                          "& .MuiDataGrid-row:hover": {
                            backgroundColor: "var(--tertiary-13)",
                          },
                          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                            {
                              outline: "none",
                            },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "var(--font-md)",
                            fontWeight: "var(--font-bold)",
                            borderLeft: "none",
                          },
                          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                            {
                              borderBottomLeftRadius: "4px",
                              borderBottomRightRadius: "4px",
                              backgroundColor: "var(--primary-pale)",
                              borderBottom: "none",
                              borderLeft: "none",
                            },
                          "& .MuiDataGrid-row > :first-child, & .MuiDataGrid-columnHeadersInner > * > :first-child":
                            {
                              paddingLeft: "60px",
                            },
                        }}
                      />
                      <>
                        <TextField
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon
                                  style={{ color: "var(--text-icon-regular)" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Search For Source Artifact"
                          value={searchString}
                          onChange={handleTextChange}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.defaultPrevented = true;
                              getRecords();
                            }
                          }}
                          sx={{
                            width: "350px",
                            "&:hover fieldset": {
                              backgroundColor: "#ffffff",
                              color: "#000000",
                            },
                            "&.MuiFormControl-root .MuiInputBase-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </>
                      <ShepDataGridPro
                        isDataLoaded={true}
                        rows={artifactRows}
                        columns={artifactColumns}
                        columnHeaderHeight={80}
                        rowHeight={80}
                        pagination
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 3, page: 0 },
                          },
                        }}
                        pageSizeOptions={[6]}
                        disableColumnMenu={true}
                        disableColumnFilter={true}
                        disableColumnReorder={true}
                        hideFooterSelectedRowCount={true}
                        onRowClick={(params) => {
                          handleArtifactSelected(params.row);
                        }}
                        sx={{
                          marginTop: "10px",
                          color: "var(#0c0c0c)",
                          fontSize: "var(--font-sm)",
                          fontFamily: "var(--font-family)",
                          borderTopLeftRadius: "12px",
                          borderTopRightRadius: "12px",
                          backgroundColor: "var(--default-light)",
                          border: "none",
                          "& .MuiDataGrid-cell": {
                            borderBottom: "none",
                          },
                          "& .MuiDataGrid-row": {
                            overflow: "hidden",
                          },
                          "& .MuiDataGrid-row:hover": {
                            backgroundColor: "var(--tertiary-13)",
                          },
                          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                            {
                              outline: "none",
                            },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "var(--font-md)",
                            fontWeight: "var(--font-bold)",
                            borderLeft: "none",
                          },
                          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                            {
                              borderBottomLeftRadius: "4px",
                              borderBottomRightRadius: "4px",
                              backgroundColor: "var(--primary-pale)",
                              borderBottom: "none",
                              borderLeft: "none",
                            },
                          "& .MuiDataGrid-row > :first-child, & .MuiDataGrid-columnHeadersInner > * > :first-child":
                            {
                              paddingLeft: "60px",
                            },
                        }}
                      />
                    </Stack>
                  </Container>
                  <Container sx={{ width: "90%" }}>
                    <Stack sx={{ width: "100%", paddingLeft: "0px" }}>
                      {
                        <Box
                          style={{
                            marginTop: 0,
                            width: "90%",
                          }}
                        >
                          <DialogContent>
                            <ShepTabs
                              variant="viewData"
                              value={previewTab}
                              onChange={handlePreviewTabChange}
                              aria-label="data tabs"
                              className="view-data-popup-tabs"
                            >
                              <ShepTab
                                variant="viewData"
                                label="Source Artifact"
                              />
                              <ShepTab
                                variant="viewData"
                                label="Before Processing"
                              />
                              <ShepTab
                                variant="viewData"
                                label="After Processing"
                              />
                              <ShepTab
                                variant="viewData"
                                label="Target Artifact"
                              />
                            </ShepTabs>
                            <Box className="tab-content">
                              {previewClicked ? (
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  height="100%"
                                >
                                  <CircularProgress />
                                </Box>
                              ) : (
                                <>
                                  {previewTab === 0 && (
                                    <Box component="pre" className="data-box">
                                      <div
                                        style={{
                                          color: "red",
                                          whiteSpace: "normal",
                                          overflowWrap: "break-word",
                                          wordBreak: "break-word",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {beforeProcessingDetails === null &&
                                        errorMsg !== "" &&
                                        errorMsg !== null
                                          ? errorMsg
                                          : ""}
                                      </div>
                                      {sourceArtifactDetails !== ""
                                        ? JSON.stringify(
                                            sourceArtifactDetails,
                                            null,
                                            2,
                                          )
                                        : "No Details To Show"}
                                    </Box>
                                  )}
                                  {previewTab === 1 && (
                                    <Box
                                      component="pre"
                                      className="data-box"
                                      sx={{ maxWidth: "100%" }}
                                    >
                                      <div
                                        style={{
                                          color: "red",
                                          whiteSpace: "normal",
                                          overflowWrap: "break-word",
                                          wordBreak: "break-word",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {afterProcessingDetails === null &&
                                        errorMsg !== "" &&
                                        errorMsg !== null
                                          ? errorMsg
                                          : ""}
                                      </div>
                                      {beforeProcessingDetails !== "" &&
                                      beforeProcessingDetails !== null
                                        ? JSON.stringify(
                                            JSON.parse(beforeProcessingDetails),
                                            null,
                                            2,
                                          )
                                        : "No Details To Show"}
                                    </Box>
                                  )}
                                  {previewTab === 2 && (
                                    <Box component="pre" className="data-box">
                                      <div
                                        style={{
                                          color: "red",
                                          whiteSpace: "normal",
                                          overflowWrap: "break-word",
                                          wordBreak: "break-word",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {afterProcessingDetails &&
                                        targetArtifactDetails === null &&
                                        errorMsg !== "" &&
                                        errorMsg !== null
                                          ? errorMsg
                                          : ""}
                                      </div>
                                      {afterProcessingDetails !== "" &&
                                      afterProcessingDetails !== null
                                        ? JSON.stringify(
                                            JSON.parse(afterProcessingDetails),
                                            null,
                                            2,
                                          )
                                        : "No Details To Show"}
                                    </Box>
                                  )}
                                  {previewTab === 3 && (
                                    <Box component="pre" className="data-box">
                                      <div
                                        style={{
                                          color: "red",
                                          whiteSpace: "normal",
                                          overflowWrap: "break-word",
                                          wordBreak: "break-word",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {targetArtifactDetails !== null &&
                                        errorMsg !== "" &&
                                        errorMsg !== null
                                          ? errorMsg
                                          : ""}
                                      </div>
                                      {targetArtifactDetails !== "" &&
                                      targetArtifactDetails !== null
                                        ? JSON.stringify(
                                            JSON.parse(targetArtifactDetails),
                                            null,
                                            2,
                                          )
                                        : "No Details To Show"}
                                    </Box>
                                  )}
                                </>
                              )}
                            </Box>
                          </DialogContent>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row-reverse",
                              marginTop: "10px",
                              paddingRight: "20px",
                            }}
                          >
                            <Button
                              className="btn-primary"
                              onClick={handlePreview}
                            >
                              Preview Output
                            </Button>
                          </Box>
                        </Box>
                      }
                    </Stack>
                  </Container>
                </Box>
              </div>
            </SettingsCard>
          </>
        </Stack>
      }
    </>
  );
};

export default PreviewWorkflowScript;
